import { Question, SurveyModel } from 'survey-core';
import { ComponentDataSourceService } from '../services/component-data-source/component-data-source.service';
import { LoadDataParams } from '../interfaces/DataSource/DataSourceParams.interface';
import { firstValueFrom } from 'rxjs';
import {
  parseSeriesDateEntries,
  toDateOnly,
} from '../services/component-data-source/data/event/event-schedule-form.util';

export interface DayOverride {
  start: string;
  end: string;
  location: string;
  room: string;
  manual_location: string;
  manual_location_address: string;
  manual_room_capacity: string;
}

function parseIdListField(value: unknown): number[] {
  if (Array.isArray(value)) {
    return value
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0);
      }
    } catch {
      return [];
    }
  }
  return [];
}

/** Main form `event_modules` (page 2) — used when series hidden fields are empty on edit. */
function parseEventModuleIdsFromSurvey(data: Record<string, unknown>): number[] {
  const raw = data['event_modules'];
  if (Array.isArray(raw)) {
    return raw
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
  }
  if (raw && typeof raw === 'object') {
    const list = (raw as { event_modules?: unknown }).event_modules;
    if (Array.isArray(list)) {
      return list
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
    }
  }
  return [];
}

export interface PlannerState {
  dates: string[];
  sameTime: boolean;
  defaultStart: string;
  defaultEnd: string;
  sameLocation: boolean;
  defaultLocation: string;
  defaultRoom: string;
  defaultManualLocation: string;
  defaultManualAddress: string;
  defaultManualCapacity: string;
  sameModules: boolean;
  defaultModuleIds: number[];
  defaultSubtypeIds: number[];
  sameCatering: boolean;
  defaultCatering: string;
  dayOverrides: Record<string, DayOverride>;
  dayModules: Record<string, number[]>;
  daySubtypes: Record<string, number[]>;
  dayCatering: Record<string, string>;
  locations: Array<{ guid: string; name: string }>;
  roomsByLocation: Record<string, Array<{ guid: string; name: string }>>;
  moduleOptions: Array<{ id: number; label: string }>;
  subtypeOptions: Array<{ id: number; label: string }>;
  /** Extra rooms (same location), when org setting allows — mirrors `additional_rooms` on survey. */
  additionalRoomIds: string[];
  /** Existing session GUID per calendar day (edit / linked group — must survive planner sync). */
  dateEventGuids: Record<string, string>;
}

function parseAdditionalRoomIdsFromSurveyData(
  data: Record<string, unknown>
): string[] {
  const raw = data['additional_rooms'];
  if (!Array.isArray(raw)) {
    return [];
  }
  const ids: string[] = [];
  for (const entry of raw) {
    if (typeof entry === 'string' && entry.trim()) {
      ids.push(entry.trim());
      continue;
    }
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const row = entry as Record<string, unknown>;
    for (const key of [
      'primary_extra_room',
      'sub_extra_room',
      'exception_extra_room',
      'extra_room',
      'guid',
      'room_guid',
    ]) {
      const value = row[key];
      if (typeof value === 'string' && value.trim()) {
        ids.push(value.trim());
        break;
      }
    }
  }
  return [...new Set(ids)];
}

export function getSurveyModel(question: Question): SurveyModel | null {
  const survey = question.survey;
  if (!survey) {
    return null;
  }
  return survey as SurveyModel;
}

function extractDataSourceItems(res: unknown): unknown[] {
  if (Array.isArray(res)) {
    return res;
  }
  if (res && typeof res === 'object') {
    const o = res as Record<string, unknown>;
    if (Array.isArray(o['items'])) {
      return o['items'];
    }
    if (Array.isArray(o['data'])) {
      return o['data'];
    }
  }
  return [];
}

function buildDayTimesPayload(
  state: PlannerState
): Record<string, { start: string; end: string }> {
  if (state.sameTime) {
    return {};
  }
  const payload: Record<string, { start: string; end: string }> = {};
  for (const date of state.dates) {
    payload[date] = {
      start: (state.dayOverrides[date]?.start ?? state.defaultStart).slice(0, 5),
      end: (state.dayOverrides[date]?.end ?? state.defaultEnd).slice(0, 5),
    };
  }
  return payload;
}

function buildDayLocationsPayload(
  state: PlannerState
): Record<string, Record<string, string>> {
  if (state.sameLocation) {
    return {};
  }
  const payload: Record<string, Record<string, string>> = {};
  for (const date of state.dates) {
    const o = state.dayOverrides[date];
    const location = o?.location ?? state.defaultLocation;
    if (!location) {
      continue;
    }
    payload[date] = {
      location,
      room: o?.room ?? state.defaultRoom,
      manual_location: o?.manual_location ?? state.defaultManualLocation,
      manual_location_address:
        o?.manual_location_address ?? state.defaultManualAddress,
      manual_room_capacity:
        o?.manual_room_capacity ?? state.defaultManualCapacity,
    };
  }
  return payload;
}

function setSurveyValueIfChanged(
  survey: SurveyModel,
  name: string,
  value: unknown
): void {
  const next = value === undefined || value === null ? '' : value;
  const current = survey.getValue(name);
  if (current === next) {
    return;
  }
  survey.setValue(name, next);
}

function syncParentDateFieldsFromPlanner(
  survey: SurveyModel,
  state: PlannerState
): void {
  if (state.dates.length === 0) {
    return;
  }
  const first = state.dates[0];
  const startTime = (
    state.dayOverrides[first]?.start ?? state.defaultStart
  ).slice(0, 5);
  const endTime = (
    state.dayOverrides[first]?.end ?? state.defaultEnd
  ).slice(0, 5);
  setSurveyValueIfChanged(survey, 'start_date', `${first}T${startTime}`);
  // Periode-felter er kun «første session» — resten ligger i series_dates / additional_dates.
  setSurveyValueIfChanged(survey, 'end_date', `${first}T${endTime}`);
}

function syncParentLocationFieldsFromPlanner(
  survey: SurveyModel,
  state: PlannerState
): void {
  if (!state.defaultLocation) {
    return;
  }
  setSurveyValueIfChanged(survey, 'location', state.defaultLocation);
  setSurveyValueIfChanged(survey, 'room', state.defaultRoom);
  setSurveyValueIfChanged(survey, 'manual_location', state.defaultManualLocation);
  setSurveyValueIfChanged(
    survey,
    'manual_location_address',
    state.defaultManualAddress
  );
  setSurveyValueIfChanged(
    survey,
    'manual_room_capacity',
    state.defaultManualCapacity
  );
}

const PLANNER_SURVEY_SYNC_FLAG = '__eventSeriesPlannerBulkSync';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function addDateGuid(
  guids: Record<string, string>,
  dateRaw: unknown,
  guidRaw: unknown
): void {
  const date = toDateOnly(String(dateRaw || ''));
  const guid = typeof guidRaw === 'string' ? guidRaw.trim() : '';
  if (date && UUID_REGEX.test(guid)) {
    guids[date] = guid;
  }
}

/** Collect session GUIDs already on the survey (edit) before planner overwrites series_dates. */
export function collectDateEventGuidsFromSurveyData(
  data: Record<string, unknown>
): Record<string, string> {
  const guids: Record<string, string> = {};

  for (const entry of parseSeriesDateEntries(data)) {
    if (entry.series_date_guid) {
      addDateGuid(guids, entry.date, entry.series_date_guid);
    }
  }

  const additional = data['additional_dates'];
  if (Array.isArray(additional)) {
    for (const row of additional) {
      if (!row || typeof row !== 'object') {
        continue;
      }
      const r = row as Record<string, unknown>;
      addDateGuid(guids, r['sub_event_date'], r['sub_event_guid']);
    }
  }

  const subEvents = data['sub_events'];
  if (Array.isArray(subEvents)) {
    for (const row of subEvents) {
      if (!row || typeof row !== 'object') {
        continue;
      }
      const r = row as Record<string, unknown>;
      addDateGuid(guids, r['sub_event_date'], r['sub_event_guid'] ?? r['guid']);
    }
  }

  const primary = data['primary_session_guid'];
  const startDay = toDateOnly(String(data['start_date'] || ''));
  if (startDay && typeof primary === 'string' && UUID_REGEX.test(primary)) {
    if (!guids[startDay]) {
      guids[startDay] = primary;
    }
  }

  const eventGuid = data['event_guid'];
  if (startDay && typeof eventGuid === 'string' && UUID_REGEX.test(eventGuid)) {
    if (!guids[startDay]) {
      guids[startDay] = eventGuid;
    }
  }

  return guids;
}

export function syncPlannerToSurvey(question: Question, state: PlannerState): void {
  const survey = getSurveyModel(question);
  if (!survey) {
    return;
  }
  (survey as SurveyModel & { [PLANNER_SURVEY_SYNC_FLAG]?: boolean })[
    PLANNER_SURVEY_SYNC_FLAG
  ] = true;
  try {
    syncPlannerToSurveyCore(survey, state);
  } finally {
    (survey as SurveyModel & { [PLANNER_SURVEY_SYNC_FLAG]?: boolean })[
      PLANNER_SURVEY_SYNC_FLAG
    ] = false;
  }
}

export function isPlannerSyncingToSurvey(survey: SurveyModel | null): boolean {
  if (!survey) {
    return false;
  }
  return Boolean(
    (survey as SurveyModel & { [PLANNER_SURVEY_SYNC_FLAG]?: boolean })[
      PLANNER_SURVEY_SYNC_FLAG
    ]
  );
}

function syncPlannerToSurveyCore(survey: SurveyModel, state: PlannerState): void {
  const uniqueDates = [
    ...new Set(state.dates.filter((d) => !!d)),
  ].sort();
  const guidByDate: Record<string, string> = {
    ...collectDateEventGuidsFromSurveyData(survey.data as Record<string, unknown>),
    ...state.dateEventGuids,
  };
  const seriesDates = uniqueDates.map((date) => {
    const series_date_guid = guidByDate[date];
    if (series_date_guid) {
      return { date, series_date_guid };
    }
    return { date };
  });
  const seriesDatesJson = JSON.stringify(seriesDates);
  setSurveyValueIfChanged(
    survey,
    'series_planner_active',
    state.dates.length > 0 ? 'true' : 'false'
  );
  setSurveyValueIfChanged(survey, 'series_dates', seriesDatesJson);
  setSurveyValueIfChanged(
    survey,
    'series_planning_mode',
    'standard_with_exceptions'
  );
  setSurveyValueIfChanged(survey, 'series_default_start_time', state.defaultStart);
  setSurveyValueIfChanged(survey, 'series_default_end_time', state.defaultEnd);
  setSurveyValueIfChanged(
    survey,
    'series_same_time',
    state.sameTime ? 'true' : 'false'
  );
  setSurveyValueIfChanged(
    survey,
    'series_same_location',
    state.sameLocation ? 'true' : 'false'
  );
  setSurveyValueIfChanged(
    survey,
    'series_same_modules',
    state.sameModules ? 'true' : 'false'
  );
  setSurveyValueIfChanged(
    survey,
    'series_same_catering',
    state.sameCatering ? 'true' : 'false'
  );
  setSurveyValueIfChanged(
    survey,
    'series_default_catering',
    state.defaultCatering || ''
  );
  setSurveyValueIfChanged(
    survey,
    'series_default_module_ids',
    JSON.stringify(state.defaultModuleIds)
  );
  setSurveyValueIfChanged(
    survey,
    'series_default_subtype_ids',
    JSON.stringify(state.defaultSubtypeIds)
  );
  setSurveyValueIfChanged(
    survey,
    'series_day_times',
    JSON.stringify(buildDayTimesPayload(state))
  );
  setSurveyValueIfChanged(
    survey,
    'series_day_locations',
    JSON.stringify(buildDayLocationsPayload(state))
  );
  syncParentDateFieldsFromPlanner(survey, state);
  setSurveyValueIfChanged(survey, 'series_default_location', state.defaultLocation);
  setSurveyValueIfChanged(survey, 'series_default_room', state.defaultRoom);
  setSurveyValueIfChanged(
    survey,
    'series_default_manual_location',
    state.defaultManualLocation
  );
  setSurveyValueIfChanged(
    survey,
    'series_default_manual_location_address',
    state.defaultManualAddress
  );
  setSurveyValueIfChanged(
    survey,
    'series_default_manual_room_capacity',
    state.defaultManualCapacity
  );
  syncParentLocationFieldsFromPlanner(survey, state);
  const additionalRoomsPayload = state.additionalRoomIds.map((guid) => ({
    primary_extra_room: guid,
  }));
  setSurveyValueIfChanged(survey, 'additional_rooms', additionalRoomsPayload);

  let modulesJson = '{}';
  if (!state.sameModules) {
    const modulesPayload: Record<
      string,
      { module_ids: number[]; subtype_ids: number[] }
    > = {};
    for (const date of state.dates) {
      const module_ids = state.dayModules[date] ?? [];
      const subtype_ids = state.daySubtypes[date] ?? [];
      if (module_ids.length > 0) {
        modulesPayload[date] = { module_ids, subtype_ids: [] };
      }
    }
    modulesJson = JSON.stringify(modulesPayload);
  }
  setSurveyValueIfChanged(survey, 'series_day_modules', modulesJson);

  let cateringJson = '{}';
  if (!state.sameCatering) {
    const cateringPayload: Record<string, string> = {};
    for (const date of state.dates) {
      const value = state.dayCatering[date] ?? state.defaultCatering;
      if (value) {
        cateringPayload[date] = value;
      }
    }
    cateringJson = JSON.stringify(cateringPayload);
  }
  setSurveyValueIfChanged(survey, 'series_day_catering', cateringJson);
  if (state.defaultCatering) {
    setSurveyValueIfChanged(survey, 'catering', state.defaultCatering);
  }
}

function emptyDayOverride(state: PlannerState): DayOverride {
  return {
    start: state.defaultStart,
    end: state.defaultEnd,
    location: state.defaultLocation,
    room: state.defaultRoom,
    manual_location: state.defaultManualLocation,
    manual_location_address: state.defaultManualAddress,
    manual_room_capacity: state.defaultManualCapacity,
  };
}

export function loadStateFromSurvey(question: Question): PlannerState {
  const survey = getSurveyModel(question);
  const data = survey?.data ?? {};
  const surveyRecord = data as Record<string, unknown>;
  const entries = parseSeriesDateEntries(surveyRecord);
  const dates = [
    ...new Set(entries.map((e) => e.date).filter((d) => !!d)),
  ].sort();
  const dateEventGuids = collectDateEventGuidsFromSurveyData(surveyRecord);
  const dayModules: Record<string, number[]> = {};
  const daySubtypes: Record<string, number[]> = {};
  const dayOverrides: Record<string, DayOverride> = {};

  const defaultLocation = String(
    data['series_default_location'] || data['location'] || ''
  );
  const defaultRoom = String(data['series_default_room'] || data['room'] || '');
  const defaultManualLocation = String(
    data['series_default_manual_location'] || data['manual_location'] || ''
  );
  const defaultManualAddress = String(
    data['series_default_manual_location_address'] ||
      data['manual_location_address'] ||
      ''
  );
  const defaultManualCapacity = String(
    data['series_default_manual_room_capacity'] ||
      data['manual_room_capacity'] ||
      ''
  );

  const rawDayTimes = (data as Record<string, unknown>)['series_day_times'];
  if (typeof rawDayTimes === 'string' && rawDayTimes.trim() !== '') {
    try {
      const parsed = JSON.parse(rawDayTimes) as Record<
        string,
        { start?: string; end?: string }
      >;
      for (const [date, times] of Object.entries(parsed)) {
        if (!times || typeof times !== 'object') {
          continue;
        }
        dayOverrides[date] = {
          start: String(times.start || '12:00').slice(0, 5),
          end: String(times.end || '13:00').slice(0, 5),
          location: defaultLocation,
          room: defaultRoom,
          manual_location: defaultManualLocation,
          manual_location_address: defaultManualAddress,
          manual_room_capacity: defaultManualCapacity,
        };
      }
    } catch {
      /* keep empty */
    }
  }

  const rawDayLocs = (data as Record<string, unknown>)['series_day_locations'];
  if (typeof rawDayLocs === 'string' && rawDayLocs.trim() !== '') {
    try {
      const parsed = JSON.parse(rawDayLocs) as Record<
        string,
        Record<string, string>
      >;
      for (const [date, loc] of Object.entries(parsed)) {
        if (!loc || typeof loc !== 'object') {
          continue;
        }
        const base = dayOverrides[date] ?? {
          start: String(data['series_default_start_time'] || '12:00').slice(0, 5),
          end: String(data['series_default_end_time'] || '13:00').slice(0, 5),
          location: defaultLocation,
          room: defaultRoom,
          manual_location: defaultManualLocation,
          manual_location_address: defaultManualAddress,
          manual_room_capacity: defaultManualCapacity,
        };
        dayOverrides[date] = {
          ...base,
          location: String(loc['location'] || defaultLocation),
          room: String(loc['room'] || defaultRoom),
          manual_location: String(
            loc['manual_location'] || defaultManualLocation
          ),
          manual_location_address: String(
            loc['manual_location_address'] || defaultManualAddress
          ),
          manual_room_capacity: String(
            loc['manual_room_capacity'] || defaultManualCapacity
          ),
        };
      }
    } catch {
      /* keep empty */
    }
  }

  const resolvedSameTime = data['series_same_time'] !== 'false';
  const resolvedSameLocation = data['series_same_location'] !== 'false';
  const resolvedSameModules = data['series_same_modules'] !== 'false';
  const resolvedSameCatering = data['series_same_catering'] !== 'false';
  const defaultCatering = String(
    data['series_default_catering'] || data['catering'] || 'irrelevant'
  );
  let defaultModuleIds = parseIdListField(data['series_default_module_ids']);
  let defaultSubtypeIds = parseIdListField(data['series_default_subtype_ids']);
  const dayCatering: Record<string, string> = {};

  if (
    typeof (data as Record<string, unknown>)['series_day_modules'] === 'string'
  ) {
    try {
      const parsed = JSON.parse(
        String((data as Record<string, unknown>)['series_day_modules'])
      );
      if (parsed && typeof parsed === 'object') {
        for (const [date, val] of Object.entries(parsed)) {
          const v = val as { module_ids?: number[]; subtype_ids?: number[] };
          dayModules[date] = Array.isArray(v.module_ids) ? v.module_ids : [];
          daySubtypes[date] = Array.isArray(v.subtype_ids) ? v.subtype_ids : [];
        }
      }
    } catch {
      /* keep empty */
    }
  }

  if (defaultModuleIds.length === 0) {
    defaultModuleIds = parseEventModuleIdsFromSurvey(data as Record<string, unknown>);
  }

  if (
    defaultModuleIds.length === 0 &&
    defaultSubtypeIds.length === 0 &&
    dates.length > 0
  ) {
    const first = dates[0];
    defaultModuleIds = [...(dayModules[first] ?? [])];
    defaultSubtypeIds = [...(daySubtypes[first] ?? [])];
  }

  if (resolvedSameModules && defaultModuleIds.length > 0 && dates.length > 0) {
    for (const d of dates) {
      if (!dayModules[d] || dayModules[d].length === 0) {
        dayModules[d] = [...defaultModuleIds];
      }
    }
  }

  const rawDayCatering = (data as Record<string, unknown>)['series_day_catering'];
  if (typeof rawDayCatering === 'string' && rawDayCatering.trim() !== '') {
    try {
      const parsed = JSON.parse(rawDayCatering) as Record<string, string>;
      if (parsed && typeof parsed === 'object') {
        for (const [date, value] of Object.entries(parsed)) {
          if (value) {
            dayCatering[date] = String(value);
          }
        }
      }
    } catch {
      /* keep empty */
    }
  }

  if (resolvedSameCatering && dates.length > 0) {
    for (const d of dates) {
      if (!dayCatering[d]) {
        dayCatering[d] = defaultCatering;
      }
    }
  }

  return {
    dates,
    sameTime: resolvedSameTime,
    defaultStart: String(data['series_default_start_time'] || '12:00').slice(
      0,
      5
    ),
    defaultEnd: String(data['series_default_end_time'] || '13:00').slice(0, 5),
    sameLocation: resolvedSameLocation,
    sameModules: resolvedSameModules,
    sameCatering: resolvedSameCatering,
    defaultCatering,
    defaultModuleIds,
    defaultSubtypeIds,
    defaultLocation,
    defaultRoom,
    defaultManualLocation,
    defaultManualAddress,
    defaultManualCapacity,
    dayOverrides,
    dayModules,
    daySubtypes,
    dayCatering,
    locations: [],
    roomsByLocation: {},
    moduleOptions: [],
    subtypeOptions: [],
    additionalRoomIds: parseAdditionalRoomIdsFromSurveyData(surveyRecord),
    dateEventGuids,
  };
}

function mapDualPanelOptions(res: unknown): Array<{ id: number; label: string }> {
  return extractDataSourceItems(res)
    .map((item) => {
      const row = item as {
        value?: unknown;
        id?: unknown;
        name?: string;
        label?: string;
      };
      const id = Number(row.value ?? row.id);
      return {
        id,
        label: String(row.name ?? row.label ?? row.value ?? id),
      };
    })
    .filter((item) => Number.isFinite(item.id) && item.id > 0);
}

export async function loadPlannerLocationList(
  service: ComponentDataSourceService
): Promise<Array<{ guid: string; name: string }>> {
  const locations: Array<{ guid: string; name: string }> = [];
  const params: LoadDataParams = {
    dataSourceKey: 'locations',
    filters: [
      { column: 'pageSize', value: 500, operator: 'equals' },
      { column: 'pageIndex', value: 0, operator: 'equals' },
      // Only guid + locations_name are used: skip per-user progress/validation on the server.
      { column: 'lightweight', value: 1, operator: 'equals' },
    ],
  };
  const locObs = service.executeDataSource('locations', params);
  if (!locObs) {
    return locations;
  }
  const locRes = await firstValueFrom(locObs);
  const locArray = extractDataSourceItems(locRes);
  for (const loc of locArray) {
    const guid = String((loc as { guid?: string }).guid || '');
    const name = String(
      (loc as { locations_name?: string }).locations_name || guid
    );
    if (guid) {
      locations.push({ guid, name });
    }
  }
  return locations;
}

/** Rooms for one location — loaded on demand when user picks a lokation. */
export async function loadPlannerRoomsForLocation(
  service: ComponentDataSourceService,
  locationGuid: string
): Promise<Array<{ guid: string; name: string }>> {
  if (!locationGuid || locationGuid === 'manual') {
    return [];
  }
  const roomsObs = service.executeDataSource('locations_rooms', {
    dataSourceKey: 'locations_rooms',
    queryParams: { location: locationGuid },
  });
  if (!roomsObs) {
    return [];
  }
  try {
    const roomsRes = await firstValueFrom(roomsObs);
    const roomArray = extractDataSourceItems(roomsRes);
    return roomArray
      .map((room) => ({
        guid: String((room as { guid?: string }).guid || ''),
        name: String((room as { room_name?: string }).room_name || ''),
      }))
      .filter((room) => room.guid);
  } catch {
    return [];
  }
}

export async function loadPlannerModuleOptions(
  service: ComponentDataSourceService
): Promise<Array<{ id: number; label: string }>> {
  const obs = service.executeDataSource('dual_panel_modules', {
    dataSourceKey: 'dual_panel_modules',
    queryParams: { pageSize: 200 },
  });
  if (!obs) {
    return [];
  }
  const res = await firstValueFrom(obs);
  return mapDualPanelOptions(res).slice(0, 250);
}

export async function loadPlannerSubtypeOptions(
  service: ComponentDataSourceService
): Promise<Array<{ id: number; label: string }>> {
  const obs = service.executeDataSource('dual_panel_subtypes', {
    dataSourceKey: 'dual_panel_subtypes',
    queryParams: { pageSize: 200 },
  });
  if (!obs) {
    return [];
  }
  const res = await firstValueFrom(obs);
  return mapDualPanelOptions(res).slice(0, 250);
}

export function toIsoDateOnly(value: Date | null): string {
  if (!value || Number.isNaN(value.getTime())) {
    return '';
  }
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Inclusive local-calendar days from `fromIso` through `toIso` (YYYY-MM-DD). */
export function isoDatesInInclusiveRange(fromIso: string, toIso: string): string[] {
  const parse = (iso: string): Date | null => {
    const parts = iso.split('-').map((p) => parseInt(p, 10));
    if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) {
      return null;
    }
    const dt = new Date(parts[0], parts[1] - 1, parts[2]);
    return Number.isNaN(dt.getTime()) ? null : dt;
  };
  const start = parse(fromIso);
  const end = parse(toIso);
  if (!start || !end || start > end) {
    return [];
  }
  const out: string[] = [];
  const cur = new Date(start);
  while (cur <= end) {
    out.push(toIsoDateOnly(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export function formatPlannerDateLabel(isoDate: string): string {
  const parts = isoDate.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) {
    return isoDate;
  }
  const dt = new Date(parts[0], parts[1] - 1, parts[2]);
  return dt.toLocaleDateString('da-DK', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export { emptyDayOverride };
